import { useQuery } from "@apollo/client";
import { parseError } from "apollo";
import { NavigationBar } from "components/base/base";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import SearchBar from "components/base/SearchBar";
import Spinner, { InlineSpinner } from "components/base/Spinner";
import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  FETCH_STOCK_SUPPLIER_OVERVIEW,
  FETCH_SUPPLIER_CHINA_STOCK_PRODUCTS,
} from "./graphql";

const renderPlans = (plans) => {
  if (!Array.isArray(plans) || plans.length === 0) return null;

  return (
    <div className="text-[11px] opacity-70 mt-1 space-y-1 text-right">
      {plans.map((plan) => (
        <div key={plan.id ?? `${plan.name}-${plan.qty}`}>
          {plan.name} ({Math.abs(plan.qty ?? 0)})
        </div>
      ))}
    </div>
  );
};

const SupplierStocksPage = () => {
  const [selectedIds, setSelectedIds] = useState([]);
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams({ q: "" });
  const params = Object.fromEntries(searchParams.entries());
  const q = params.q || "";
  const { loading, error, data } = useQuery(
    FETCH_SUPPLIER_CHINA_STOCK_PRODUCTS,
    {
      variables: { supplierId: id },
    },
  );
  const fetchOverview = useQuery(FETCH_STOCK_SUPPLIER_OVERVIEW, {
    variables: { supplierId: id },
  });

  if (loading) return <Spinner />;
  if (error) return <Errors error={parseError(error)} />;

  const products = data.products.results;

  const overview = fetchOverview.data?.stockSupplierOverview
    ? JSON.parse(fetchOverview.data.stockSupplierOverview)
    : {};
  const rows = products.map((i) => {
    const item = overview[i.id] || {};
    return { ...i, ...item };
  });
  const normalizedQ = q.trim().toLowerCase();
  const filteredRows = rows.filter((row) => {
    if (!normalizedQ) return true;
    return (
      String(row.number || "")
        .toLowerCase()
        .includes(normalizedQ) ||
      String(row.name || "")
        .toLowerCase()
        .includes(normalizedQ)
    );
  });
  const isAllFilteredSelected =
    filteredRows.length > 0 &&
    filteredRows.every((row) => selectedIds.includes(row.id));

  console.log("rows?", rows);

  return (
    <div className="flex-1 overflow-auto flex flex-col">
      <NavigationBar
        title={data.supplier.nickName}
        rightButtons={
          <div className="flex items-center space-x-6">
            {fetchOverview.loading && (
              <InlineSpinner size={16} text="Loading stock data..." />
            )}
            {fetchOverview.error && <Errors error={fetchOverview.error} />}
          </div>
        }
      />

      <div className="p-6 flex-1 overflow-auto flex flex-col space-y-4">
        <div>
          <SearchBar
            value={q}
            placeholder="Search by product number or name"
            onChange={(nextQ) => setSearchParams({ ...params, q: nextQ })}
          />
        </div>

        <div className="card p-0 max-h-full overflow-auto">
          <div className="h-full overflow-auto relative">
            <table className="with-line">
              <thead className="whitespace-nowrap">
                <tr className="sticky top-0 backdrop-blur-2xl z-10 bg-gray-100/50 backdrop-opacity-95">
                  <th className="px-4 py-3 align-middle">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        className="opacity-70"
                        checked={isAllFilteredSelected}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedIds(filteredRows.map((row) => row.id));
                          } else {
                            setSelectedIds([]);
                          }
                        }}
                      />
                      {selectedIds.length > 0 ? (
                        <div className="flex items-center space-x-3">
                          <div>{selectedIds.length} product(s) selected</div>
                          <Button onClick={() => setSelectedIds([])}>
                            Clear
                          </Button>
                        </div>
                      ) : (
                        <div>Product ({filteredRows.length})</div>
                      )}
                    </div>
                  </th>
                  <th className="px-4 text-right align-middle">Odoo</th>
                  <th className="px-4 text-right align-middle">Odoo LTD</th>
                  <th className="px-4 text-right align-middle">SS</th>
                  <th className="px-4 text-right align-middle">
                    Confirmed Plans
                  </th>
                  <th className="px-4 text-right align-middle">
                    Not Confirmed Plans
                  </th>
                  <th className="px-4 text-right align-middle">FCST</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => (
                  <tr
                    key={row.id}
                    className=" hover:bg-sky-50 dark:hover:bg-sky-900/50"
                  >
                    <td className="px-4 align-middle">
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          className="opacity-70"
                          checked={selectedIds.includes(row.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedIds([...selectedIds, row.id]);
                            } else {
                              setSelectedIds(
                                selectedIds.filter(
                                  (selectedId) => selectedId !== row.id,
                                ),
                              );
                            }
                          }}
                        />
                        <Link to={`${row.id}`}>
                          [{row.number}] {row.name}
                        </Link>
                      </div>
                    </td>
                    <td className="px-4 text-right align-middle">
                      {row.odoo_fcst ?? "-"}
                    </td>
                    <td className="px-4 text-right align-middle">
                      {row.odoo_ltd_fcst ?? "-"}
                    </td>
                    <td className="px-4 text-right align-middle">
                      {row.ss ?? "-"}
                    </td>
                    <td className="px-4 text-right align-middle">
                      <div>{row.planning_qty ?? "-"}</div>
                      {renderPlans(row.confirmed_plans)}
                    </td>
                    <td className="px-4 text-right align-middle">
                      <div>{row.not_confirmed_qty ?? "-"}</div>
                      {renderPlans(row.not_confirmed_plans)}
                    </td>
                    <td className="px-4 text-right align-middle">
                      {row.fcst_qty ?? "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupplierStocksPage;
